/**
 * The small pieces, sharing the server rendered tier's class names.
 *
 * Reusing trimeda.css rather than restyling is not laziness. The stylesheet
 * carries the contrast floors, the right to left rules and the bidi isolation
 * on figures, all of which were worked out against real Arabic layout. A
 * parallel set of React styles would be a second place for those to drift.
 */
import type { ReactNode } from 'react'

export type Tone = 'good' | 'warn' | 'bad' | 'info' | 'muted' | null

/** Colour family by value rather than by column, so the same status word reads
 *  the same wherever it appears. Mirrors render.badge_tone on the server. */
const GOOD = new Set(['active', 'verified', 'published', 'qualified', 'allowed',
  'success', 'yes', 'true', 'closed', 'disbursed', 'accepted', 'offered',
  'ready', 'strong', 'clear', 'production_live', 'delivered', 'completed',
  'done', 'sharia', 'authoritative_api', 'nafath', 'wathq', 'zatca', 'built'])
const WARN = new Set(['pending', 'draft', 'in_review', 'info_requested',
  'routed', 'viewed', 'approaching', 'developing', 'half_open', 'retrying',
  'sandbox', 'indeterminate', 'near_miss', 'unverified', 'pending_review',
  'queued', 'waiting', 'paused', 'self_declared', 'declared', 'uat', 'testing',
  'open', 'assigned', 'in_progress', 'sandbox_tested', 'sandbox_access',
  'placeholder'])
const BAD = new Set(['failed', 'declined', 'revoked', 'expired',
  'not_qualified', 'error', 'denied_no_consent', 'denied_scope',
  'denied_purpose', 'denied_expired', 'suspended', 'withdrawn', 'not_ready',
  'circuit_open', 'timeout', 'server_error', 'client_error', 'auth_failed',
  'cancelled', 'breached', 'not_started', 'dead_lettered', 'escalated',
  'consent_denied', 'mapping_error', 'rate_limited', 'no'])

export function toneOf(value: unknown): Tone {
  if (value === null || value === undefined) return 'muted'
  const v = String(value).toLowerCase()
  if (GOOD.has(v)) return 'good'
  if (BAD.has(v)) return 'bad'
  if (WARN.has(v)) return 'warn'
  return 'info'
}

export function Badge({ children, tone = 'info' }:
                      { children: ReactNode; tone?: Tone }) {
  if (children === null || children === undefined || children === '') return null
  return <span className={`b ${tone || 'info'}`}>{children}</span>
}

export function Note({ kind = 'info', children }:
                     { kind?: 'info' | 'good' | 'warn' | 'bad'; children: ReactNode }) {
  return <div className={`note ${kind}`}>{children}</div>
}

export function Meter({ pct }: { pct: number | null | undefined }) {
  const width = Math.max(0, Math.min(100, Number(pct ?? 0)))
  return <div className="meter"><i style={{ width: `${width}%` }} /></div>
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return <div className="empty"><b>{title}</b>{children}</div>
}

export function KV({ rows }: { rows: [ReactNode, ReactNode][] }) {
  return (
    <dl className="kv">
      {rows.map(([k, v], i) => (
        <div key={i}><dt>{k}</dt><dd>{v ?? '—'}</dd></div>
      ))}
    </dl>
  )
}

export function Spinner({ what }: { what?: string }) {
  return (
    <div className="empty" role="status" aria-live="polite">
      <b>Loading{what ? ` ${what}` : ''}</b>
    </div>
  )
}

/** A dashboard tile. The figure is pre-formatted by the caller, because the
 *  caller knows whether it is money, a count or a percentage. */
export function Tile({ label: text, value, sub, tone, onExplain }: {
  label: string
  value: ReactNode
  sub?: ReactNode
  tone?: Tone
  onExplain?: () => void
}) {
  return (
    <div className={`tile ${tone || ''}`}>
      <div className="tile-label">
        {text}
        {onExplain && (
          <button className="ibtn" type="button" onClick={onExplain}
                  aria-label={`Purpose, logic and calculation: ${text}`}
                  title="Purpose, logic and calculation">!</button>
        )}
      </div>
      <div className="tile-value">{value}</div>
      {sub && <div className="tile-sub">{sub}</div>}
    </div>
  )
}

export function Tiles({ children }: { children: ReactNode }) {
  return <div className="tiles">{children}</div>
}
